import axios, { type AxiosError, type AxiosInstance } from "axios";

import { getActiveWorkspaceId } from "@/lib/active-workspace";
import type { BaseResponse, PaginationMeta } from "@/types/api";

// Empty in dev (Vite proxies `/api` -> backend, same origin so cookies work).
// In prod, the full backend host (e.g. https://api.tdax.com.br).
const BASE_URL = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "";

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  // Double-submit CSRF: o backend seta o cookie `tdax_csrf` (não httpOnly) no login
  // e exige o header `X-CSRF-Token` batendo com ele nos métodos mutadores.
  withXSRFToken: true,
  xsrfCookieName: "tdax_csrf",
  xsrfHeaderName: "X-CSRF-Token",
});

// Active workspace sent on every request via X-Workspace-Id (single source of
// truth in lib/active-workspace, shared with WorkspaceContext).
api.interceptors.request.use((config) => {
  const wsId = getActiveWorkspaceId();
  if (wsId != null) {
    config.headers["X-Workspace-Id"] = String(wsId);
  }
  return config;
});

// Quando o refresh falha (sessão perdida de vez), avisamos o AuthProvider p/ limpar
// o /me — aí o ProtectedLayout redireciona pro /login. Páginas públicas não reagem.
let onAuthLost: (() => void) | null = null;

export function registerAuthLostHandler(cb: (() => void) | null): void {
  onAuthLost = cb;
}

// Códigos de gate que o backend devolve com 403 quando o estado da sessão muda
// (e-mail/telefone não verificado, ou platform admin sem MFA). Espelham
// `VerificationRequiredError`/`MfaSetupRequiredError` em core/exceptions.py.
const GATE_ERROR_CODES = new Set(["VERIFICATION_REQUIRED", "MFA_SETUP_REQUIRED"]);

// Quando um 403 de gate aparece no meio da sessão, avisamos o AuthProvider p/
// reavaliar o /me — aí o ProtectedLayout leva o usuário à tela de verificação/MFA
// em vez de ele ver um erro avulso.
let onGate: (() => void) | null = null;

export function registerGateHandler(cb: (() => void) | null): void {
  onGate = cb;
}

function gateErrorCode(error: AxiosError): string | null {
  if (error.response?.status !== 403) return null;
  const body = error.response.data as BaseResponse<unknown> | undefined;
  const code = body?.error?.code;
  return code != null && GATE_ERROR_CODES.has(code) ? code : null;
}

// On 401, try /auth/refresh once (rotating refresh) then replay the request.
let refreshing: Promise<void> | null = null;

function attemptRefresh(): Promise<void> {
  if (!refreshing) {
    refreshing = api
      .post("/api/v1/auth/refresh", null, { _skipRefresh: true } as never)
      .then(() => {
        refreshing = null;
      })
      .catch((err) => {
        refreshing = null;
        throw err;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (typeof error.config & { _retry?: boolean; _skipRefresh?: boolean })
      | undefined;
    if (!original || original._retry || original._skipRefresh) {
      return Promise.reject(error);
    }
    // 403 de gate (verificação/MFA) no meio da sessão → reavalia o /me p/ o
    // ProtectedLayout levar o usuário à tela certa, sem replay do request.
    if (gateErrorCode(error) != null) {
      onGate?.();
      return Promise.reject(error);
    }
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    try {
      await attemptRefresh();
    } catch (refreshErr) {
      // Refresh falhou → sessão perdida → limpa o auth (ProtectedLayout → /login).
      onAuthLost?.();
      return Promise.reject(refreshErr);
    }
    original._retry = true;
    return api.request(original);
  },
);

/** Unwraps the BaseResponse<T> envelope, throwing on failure. */
export async function unwrap<T>(promise: Promise<{ data: BaseResponse<T> }>): Promise<T> {
  const response = await promise;
  if (!response.data.success || response.data.data == null) {
    throw new Error(response.data.error?.message ?? "Erro desconhecido");
  }
  return response.data.data;
}

/** Como `unwrap`, mas preserva o `meta` de paginação (FE-010): listas paginadas
 *  precisam do `meta.total` para contadores/KPIs refletirem o total real, não só
 *  a página carregada. */
export interface Paged<T> {
  data: T;
  meta?: PaginationMeta;
}

export async function unwrapWithMeta<T>(
  promise: Promise<{ data: BaseResponse<T> }>,
): Promise<Paged<T>> {
  const response = await promise;
  if (!response.data.success || response.data.data == null) {
    throw new Error(response.data.error?.message ?? "Erro desconhecido");
  }
  return { data: response.data.data, meta: response.data.meta };
}

/** Friendly message from the envelope (details validation/permission/conflict). */
export function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err != null && "response" in err) {
    const body = (err as { response?: { data?: BaseResponse<unknown> } }).response?.data;
    if (body?.error) {
      const detailErrors = (
        body.error.details as { errors?: Array<{ loc?: unknown[]; msg?: string }> } | undefined
      )?.errors;
      if (detailErrors && detailErrors.length > 0) {
        return detailErrors
          .map((e) => `${(e.loc ?? []).join(".")}: ${e.msg ?? "invalido"}`)
          .join("; ");
      }
      return body.error.message;
    }
  }
  if (err instanceof Error) return err.message;
  return "Erro desconhecido";
}
